import { useEffect, useRef, useState } from "react";
import { Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, apiError } from "@/api";
import { useLang } from "@/i18n";
import { Seo } from "@/components/Seo";
import { ConciergeSidebar } from "@/components/concierge/ConciergeSidebar";
import { ChatPanel } from "@/components/concierge/ChatPanel";
import { ItineraryPanel } from "@/components/concierge/ItineraryPanel";
import { ServiceTabs } from "@/components/concierge/ServiceTabs";
import { ServiceDetail } from "@/components/concierge/ServiceDetail";
import { SwapDialog } from "@/components/concierge/SwapDialog";
import { TripSummary } from "@/components/concierge/TripSummary";
import { svcName, stopCity } from "@/components/concierge/utils";

const newSid = () => `cc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export default function Concierge() {
    const { lang, t } = useLang();
    const tc = t.concierge;
    const [sid] = useState(() => localStorage.getItem("tv_concierge_sid") || (() => { const s = newSid(); localStorage.setItem("tv_concierge_sid", s); return s; })());
    const [messages, setMessages] = useState([]);
    const [proposal, setProposal] = useState(null);
    const [trip, setTrip] = useState({ stays: [], experiences: [], transfers: [] });
    const [catalog, setCatalog] = useState(null);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [contact, setContact] = useState({ customer_name: "", customer_email: "", customer_phone: "", privacy_consent: false });
    const [sendingReq, setSendingReq] = useState(false);
    const [doneReq, setDoneReq] = useState(null);
    const [voiceOn, setVoiceOn] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [activeTab, setActiveTab] = useState("stays");
    const [activeIdx, setActiveIdx] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [swapItem, setSwapItem] = useState(null);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);
    const voiceOnRef = useRef(false);
    const scrollRef = useRef(null);
    const tabsRef = useRef(null);

    const LANG_BCP = { it: "it-IT", en: "en-US", fr: "fr-FR", es: "es-ES", de: "de-DE", pt: "pt-PT", "zh-hans": "zh-CN", "zh-hant": "zh-TW", ar: "ar-SA", ru: "ru-RU", ja: "ja-JP", ko: "ko-KR", hi: "hi-IN", tr: "tr-TR", nl: "nl-NL", pl: "pl-PL" };

    const speak = (text) => {
        if (!voiceOnRef.current || !("speechSynthesis" in window) || !text) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text.slice(0, 700));
        u.lang = LANG_BCP[lang] || "it-IT";
        window.speechSynthesis.speak(u);
    };
    const toggleVoice = () => {
        const next = !voiceOn;
        setVoiceOn(next);
        voiceOnRef.current = next;
        if (!next && "speechSynthesis" in window) window.speechSynthesis.cancel();
    };

    /* Dettatura vocale: registrazione MediaRecorder → Whisper lato server (whisper-1). */
    const toggleRec = async () => {
        if (recording) { mediaRef.current?.stop(); return; }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => chunksRef.current.push(e.data);
            rec.onstop = async () => {
                stream.getTracks().forEach((tr) => tr.stop());
                setRecording(false);
                setTranscribing(true);
                try {
                    const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                    const fd = new FormData();
                    fd.append("file", blob, "voce.webm");
                    const r = await api.post("/public/concierge/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    const text = (r.data.text || "").trim();
                    if (text) setInput((v) => (v ? `${v} ${text}` : text));
                } catch (e) {
                    toast.error(apiError(e, lang));
                } finally {
                    setTranscribing(false);
                }
            };
            rec.start();
            mediaRef.current = rec;
            setRecording(true);
        } catch {
            toast.error(tc.micDenied);
        }
    };

    useEffect(() => {
        api.get(`/public/concierge/${sid}`)
            .then((r) => {
                const msgs = r.data.messages || [];
                setMessages(msgs.length ? msgs : [{ role: "assistant", text: t.concierge.greeting }]);
                setProposal(r.data.proposal || null);
                if (r.data.trip) setTrip(r.data.trip);
            })
            .catch(() => setMessages([{ role: "assistant", text: t.concierge.greeting }]));
        api.get("/public/builder/catalog").then((r) => setCatalog(r.data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sid]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, busy]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "", streaming: true }]);
        setBusy(true);
        try {
            const resp = await fetch(`${API}/public/concierge/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sid, message: msg, language: lang }),
            });
            if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { value, done } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    let ev;
                    try { ev = JSON.parse(part.slice(6)); } catch { continue; }
                    if (ev.type === "delta") {
                        setMessages((m) => {
                            const copy = [...m];
                            const last = copy[copy.length - 1];
                            copy[copy.length - 1] = { ...last, text: (last.text || "") + ev.text };
                            return copy;
                        });
                    } else if (ev.type === "proposal") {
                        setProposal(ev.proposal);
                        if (ev.trip) setTrip(ev.trip);
                        setActiveIdx(0);
                    } else if (ev.type === "error") {
                        throw new Error(ev.message);
                    }
                }
            }
            setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                let text = last.text || "";
                text = text.replace(/```proposal[\s\S]*?```/g, "").trim();
                copy[copy.length - 1] = { role: "assistant", text: text || tc.thinking };
                speak(text);
                return copy;
            });
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: tc.error, isError: true };
                return copy;
            });
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        const s = newSid();
        localStorage.setItem("tv_concierge_sid", s);
        window.location.reload();
    };

    /* ——— Stato viaggio condiviso: ogni modifica aggiorna backend + UI ——— */
    const mutateService = async (payload, okToast) => {
        try {
            const r = await api.post("/public/concierge/trip/service", { session_id: sid, ...payload });
            setTrip(r.data.trip);
            if (r.data.proposal) setProposal(r.data.proposal);
            if (okToast && !r.data.duplicated) toast.success(okToast);
            return r.data;
        } catch (e) {
            toast.error(apiError(e, lang));
            return null;
        }
    };
    const removeService = (item) => mutateService({ action: "remove", kind: item.kind, ref_id: item.ref_id }, tc.toastRemoved);
    const swapService = async (row) => {
        if (!swapItem) return;
        const r = await mutateService({
            action: "replace", kind: swapItem.kind, ref_id: row.ref_id,
            city_id: row.city_id || swapItem.city_id || "", replace_ref_id: swapItem.ref_id,
        }, tc.toastReplaced);
        if (r) { setSwapItem(null); setDetailItem(null); }
    };
    const mutateStop = async (payload, okToast) => {
        try {
            const r = await api.post("/public/concierge/trip/stop", { session_id: sid, ...payload });
            setProposal(r.data.proposal);
            if (r.data.trip) setTrip(r.data.trip);
            if (!r.data.duplicated) toast.success(okToast);
        } catch (e) {
            toast.error(apiError(e, lang));
        }
    };
    const addStop = (city_id) => mutateStop({ action: "add", city_id }, tc.toastStopAdded);
    const removeStop = (order) => mutateStop({ action: "remove", order }, tc.toastStopRemoved);

    const askAi = (msg) => {
        send(msg);
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    };
    const goRestaurants = () => {
        setActiveTab("restaurants");
        tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const sendRequest = async () => {
        if (contact.customer_name.trim().length < 2 || !contact.customer_email.includes("@") || contact.customer_phone.trim().length < 5 || !contact.privacy_consent) {
            toast.error(t.transfers.qRequired);
            return;
        }
        setSendingReq(true);
        try {
            const r = await api.post("/public/concierge/request", {
                session_id: sid, ...contact, language: lang, origin_url: window.location.origin,
            });
            setDoneReq(r.data);
            setShowForm(false);
        } catch (e) {
            toast.error(apiError(e, lang));
        } finally {
            setSendingReq(false);
        }
    };

    return (
        <div className="marble-bg min-h-screen pt-24 pb-20" data-testid="concierge-page">
            <Seo lang={lang} title={`${tc.pageTitle} — TOURVOYAGERS`} description={tc.pageSub} />
            <div className="mx-auto flex max-w-[110rem] items-start gap-6 px-4 sm:px-6 lg:px-8">
                <ConciergeSidebar onRestaurants={goRestaurants} />

                <main className="min-w-0 flex-1 space-y-6">
                    <ChatPanel tc={tc} messages={messages} busy={busy} input={input} setInput={setInput} send={send}
                        recording={recording} transcribing={transcribing} toggleRec={toggleRec}
                        voiceOn={voiceOn} toggleVoice={toggleVoice} reset={reset} scrollRef={scrollRef} />

                    <ItineraryPanel proposal={proposal} trip={trip} catalog={catalog}
                        activeIdx={activeIdx} setActiveIdx={setActiveIdx}
                        editMode={editMode} setEditMode={setEditMode}
                        showMap={showMap} setShowMap={setShowMap}
                        onAddStop={addStop} onRemoveStop={removeStop} onAskAi={askAi}
                        tc={tc} lang={lang} />

                    <ServiceTabs trip={trip} activeTab={activeTab} setActiveTab={setActiveTab}
                        onDetail={setDetailItem} onReplace={setSwapItem} onRemove={removeService}
                        onAskRestaurants={() => askAi(tc.restaurantsMsg)}
                        tc={tc} lang={lang} sectionRef={tabsRef} />

                    {/* Riepilogo inline su schermi piccoli (rail laterale da xl) */}
                    <div className="xl:hidden">
                        <TripSummary proposal={proposal} trip={trip} onQuote={() => setShowForm(true)} doneReq={doneReq} tc={tc} lang={lang} compact />
                    </div>
                </main>

                <div className="hidden w-72 shrink-0 xl:block">
                    <TripSummary proposal={proposal} trip={trip} onQuote={() => setShowForm(true)} doneReq={doneReq} tc={tc} lang={lang} />
                </div>
            </div>

            {/* DETTAGLIO SERVIZIO */}
            <ServiceDetail item={detailItem} tc={tc} lang={lang}
                onClose={() => setDetailItem(null)}
                onReplace={(item) => { setSwapItem(item); }}
                onRemove={removeService} />

            {/* SOSTITUZIONE DA CATALOGO REALE */}
            {swapItem && (
                <SwapDialog kind={swapItem.kind} current={swapItem}
                    cityId={swapItem.city_id || (proposal?.stops?.[0] && (catalog?.italy_destinations || []).find((d) => d.name.it.toLowerCase() === stopCity(proposal.stops[0], "it").toLowerCase())?.id)}
                    onSelect={swapService} onClose={() => setSwapItem(null)} tc={tc} lang={lang} />
            )}

            {/* MODAL RICHIESTA PREVENTIVO con riepilogo viaggio */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="concierge-req-modal" onClick={() => setShowForm(false)}>
                    <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-amber-500/30 bg-[#101012] p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="font-display text-xl text-amber-50">{tc.formTitle}</h3>
                                <p className="mt-1 text-xs text-stone-400">{tc.formSub}</p>
                            </div>
                            <button onClick={() => setShowForm(false)} data-testid="concierge-req-close" className="text-stone-500 hover:text-amber-300"><X size={18} /></button>
                        </div>

                        {proposal && (
                            <div className="mt-4 rounded-xl border border-amber-500/15 bg-amber-400/5 p-4" data-testid="req-recap">
                                <p className="text-[9px] uppercase tracking-[0.2em] text-amber-500/80 font-semibold">{tc.recapTitle}</p>
                                <p className="mt-1.5 text-xs font-semibold text-amber-100">{proposal.title}</p>
                                <p className="mt-1 text-[11px] text-stone-400">
                                    {(proposal.stops || []).map((s) => `${stopCity(s, lang)} (${s.nights} ${tc.nights})`).join(" → ")}
                                </p>
                                {[...trip.stays, ...trip.experiences, ...trip.transfers, ...(trip.restaurants || [])].length > 0 && (
                                    <ul className="mt-2 space-y-0.5 border-t border-amber-500/10 pt-2 text-[11px] text-stone-400">
                                        {[...trip.stays, ...trip.experiences, ...trip.transfers, ...(trip.restaurants || [])].map((it) => (
                                            <li key={`${it.kind}-${it.ref_id}`}>· {svcName(it, lang)}{it.price != null ? ` — € ${Number(it.price).toLocaleString("it-IT")}` : (it.meta?.price_level ? ` — ${it.meta.price_level}` : "")}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        <div className="mt-5 space-y-3.5">
                            <input data-testid="cf-name" className="input-lux" placeholder={tc.cfName} value={contact.customer_name} onChange={(e) => setContact({ ...contact, customer_name: e.target.value })} />
                            <input data-testid="cf-email" type="email" className="input-lux" placeholder={tc.cfEmail} value={contact.customer_email} onChange={(e) => setContact({ ...contact, customer_email: e.target.value })} />
                            <input data-testid="cf-phone" className="input-lux" placeholder={tc.cfPhone} value={contact.customer_phone} onChange={(e) => setContact({ ...contact, customer_phone: e.target.value })} />
                            <label className="flex items-start gap-3 text-xs text-stone-300">
                                <input data-testid="cf-privacy" type="checkbox" className="mt-0.5 accent-amber-400" checked={contact.privacy_consent} onChange={(e) => setContact({ ...contact, privacy_consent: e.target.checked })} />
                                {tc.cfPrivacy}
                            </label>
                            <button onClick={sendRequest} disabled={sendingReq} data-testid="cf-send"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50">
                                {sendingReq ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                {sendingReq ? tc.cfSending : tc.cfSend}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
